import { scry, scryImageUrl } from "@/lib/scryClient";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { auth, db } from "@/lib/firebase";
import { addDoc, collection, serverTimestamp, writeBatch, doc, getDocs, query, orderBy } from "firebase/firestore";
import {
  parseDeckFromText,
  parseDeckFromUrl,
  enrichWithScryfall,
  type NormalDeck,
  type NormalCard
} from "@/lib/importers/deckImporters";
import * as scryApi from "@/lib/scryApiClient";

// REMOVE: enrichFullCards to avoid double enrichment on write
// import { enrichFullCards } from "@/lib/scryfall/enrichFull";

type Props = { open: boolean; onClose: () => void; };
type Destination = "collection" | "wishlist" | "binder" | "deck";
// Extend WorkingRow to include all optional metadata (already added in NormalCard)
type WorkingRow = NormalCard & {
  include: boolean;
  foil: boolean;
  for_trade: boolean;
  for_sale: boolean;
  scryfall_full?: any | null;
};

const BASIC_LANDS = new Set([
  "plains","island","swamp","mountain","forest","wastes",
  "snow-covered plains","snow-covered island","snow-covered swamp",
  "snow-covered mountain","snow-covered forest"
]);
function isBasicLand(name: string) {
  return BASIC_LANDS.has(name.toLowerCase());
}

// === shared helpers (module scope) ===
function normExactName(s: string): string {
  const n = String(s || "")
    .replace(/&(?:#x27|#39|#x2019|#8217);/gi, "'")
    .replace(/[’‘`´]/g, "'")
    .replace(/''+/g, "'")
    .replace(/"{2,}/g, '"')
    .trim();
  return n;
}
function exactQuery(nm: string): string {
  return `name:"${nm}"`;
}
function nameKey(s: string): string {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘`´]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9]+/gi, "")
    .toLowerCase();
}

// moved to module scope:
function toCardArray(payload: any): any[] {
  try {
    if (typeof payload === "string") payload = JSON.parse(payload);
    if (typeof payload?.data === "string") {
      try { payload.data = JSON.parse(payload.data); } catch {}
    }
  } catch {}
  if (!payload) return [];
  if (payload?.object === "card" && payload?.id) return [payload];
  if (payload?.data?.object === "card" && payload?.data?.id) return [payload.data];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.object === "list" && Array.isArray(payload?.data)) return payload.data;
  if (payload?.data?.object === "list" && Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.cards)) return payload.data.cards;
  if (Array.isArray(payload?.cards)) return payload.cards;
  if (Array.isArray(payload?.prints)) return payload.prints;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function sortByRelease<T extends { released_at?: string; set?: string; collector_number?: string }>(list: T[]) {
  return [...(list || [])].sort((a, b) => {
    const da = a.released_at ? Date.parse(a.released_at) : 0;
    const db = b.released_at ? Date.parse(b.released_at) : 0;
    if (db !== da) return db - da;
    const sa = a.set ?? "";
    const sb = b.set ?? "";
    if (sb !== sa) return sb.localeCompare(sa);
    const ca = parseInt((a as any).collector_number || "0", 10) || 0;
    const cb = parseInt((b as any).collector_number || "0", 10) || 0;
    return cb - ca;
  });
}

function pickFirstCard(payload: any): any | null {
  const arr = toCardArray(payload);
  return arr.length ? arr[0] : null;
}

function pickCardImagesFromFull(c: any) {
  const f = c?.card_faces?.[0]?.image_uris || {};
  const iu = c?.image_uris || {};
  return {
    small: iu.small || f.small || null,
    normal: iu.normal || f.normal || null,
    large: iu.large || f.large || null,
  };
}

// --- Backend Scryfall Cards client (variants) ---
function resolveScryCardsBase() {
  const env: any = (import.meta as any).env || {};
  const useEmu = String(env.VITE_USE_EMULATORS || "").toLowerCase() === "true";
  const explicit = env.VITE_SCRY_CARDS_URL as string | undefined;

  if (useEmu) return explicit || "http://localhost:5000/api/scry/cards";
  if (explicit && !/localhost|127\.0\.0\.1/.test(explicit)) return explicit;

  const api = env.VITE_API_BASE as string | undefined;
  if (api) return `${api.replace(/\/$/, "")}/api/scry/cards`;

  return `${globalThis.location?.origin || ""}/api/scry/cards`;
}
const SCRY_CARDS_BASE = resolveScryCardsBase();

async function scryCardsGet<T = any>(
  path: string,
  params?: Record<string, string | number | null | undefined>
): Promise<T | null> {
  if (!SCRY_CARDS_BASE) return null;
  try {
    const p = path.replace(/^\//, "");
    const q = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    const url =
      SCRY_CARDS_BASE.startsWith("http")
        ? `${SCRY_CARDS_BASE.replace(/\/$/, "")}/${p}${q}`
        : `${SCRY_CARDS_BASE.replace(/\/$/, "")}/${p}${q}`;

    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) throw new Error(`scry-cards ${path} failed: ${r.status}`);
    return (await r.json()) as T;
  } catch (e) {
    console.warn("[scry-cards] fallback (ImportDeckModal):", path, e);
    return null;
  }
}

// Feature-detect helpers from scryApiClient (align with V2Modal/QSO)
function hasFn<T extends keyof typeof scryApi>(k: T): boolean {
  return typeof (scryApi as any)?.[k] === "function";
}

// Wrap backend calls to match V2Modal behavior exactly
async function apiNamed(exact: string): Promise<any | null> {
  try {
    if (hasFn("named")) {
      return await (scryApi as any).named(exact);
    }
  } catch {}
  try {
    // fallback: backend search by name field (no !, preserves punctuation)
    const res = await scryCardsGet<any>("search", {
      q: `name:"${exact}"`,
      unique: "prints",
      order: "released",
      dir: "desc",
    });
    const arr = toCardArray(res);
    return sortByRelease(arr)[0] ?? null;
  } catch {}
  return null;
}

async function apiPrintsByOracle(oracleId: string): Promise<any[]> {
  return sortByRelease(await printsByOracleSafe(oracleId));
}

async function apiPrintsById(id: string): Promise<any[]> {
  return sortByRelease(await printsByIdSafe(id));
}

// Ensure every row has scryfall_full and backfilled fields/images (backend-first)
async function ensureFullCards(rows: WorkingRow[]): Promise<WorkingRow[]> {
  const cache = new Map<string, any>();
  const out = [...rows];
  const tasks = out.map(async (r, i) => {
    if (r.scryfall_full && r.scryfall_full.id) return;
    let full: any | null = null;

    // 1) by scryfall_id via backend
    if (r.scryfall_id) {
      if (cache.has(r.scryfall_id)) full = cache.get(r.scryfall_id);
      else {
        try { full = await scryApi.getCardById(r.scryfall_id); } catch {}
        cache.set(r.scryfall_id, full || null);
      }
    }
    // 2) by oracle_id via backend search
    if (!full && r.oracle_id) {
      try {
        const list = await scryApi.searchPrints(`oracleid:${r.oracle_id}`);
        full = sortByRelease(toCardArray(list))[0] ?? null;
      } catch {}
    }
    // 3) by name via backend search (no "!" exact; backend-only)
    if (!full && r.name) {
      try {
        const nm = normExactName(r.name);
        const res = await scryApi.searchPrints(exactQuery(nm));
        full = sortByRelease(toCardArray(res))[0] ?? null;
        // fallback to a simple phrase query if needed
        if (!full) {
          const res2 = await scryApi.searchPrints(`"${nm}"`);
          full = sortByRelease(toCardArray(res2))[0] ?? null;
        }
      } catch {}
    }

    if (!full) return;

    const imgs = pickCardImagesFromFull(full);
    out[i] = {
      ...out[i],
      scryfall_id: out[i].scryfall_id || full.id || null,
      oracle_id: out[i].oracle_id || full.oracle_id || null,
      set: out[i].set || full.set?.toUpperCase?.() || null,
      number: out[i].number || full.collector_number || null,
      layout: out[i].layout ?? full.layout ?? null,
      type_line: out[i].type_line ?? full.type_line ?? null,
      colors: out[i].colors ?? (Array.isArray(full.colors) ? full.colors : null),
      color_identity: out[i].color_identity ?? (Array.isArray(full.color_identity) ? full.color_identity : null),
      mana_value:
        out[i].mana_value ??
        (typeof full.mana_value === "number" ? full.mana_value : (typeof full.cmc === "number" ? full.cmc : null)),
      oracle_text: out[i].oracle_text ?? full.oracle_text ?? null,
      image_small: out[i].image_small ?? imgs.small,
      image_normal: out[i].image_normal ?? imgs.normal,
      image_large: out[i].image_large ?? imgs.large,
      scryfall_full: full,
    };
  });
  await Promise.all(tasks);
  return out;
}

// Ensure every row has scryfall_full and backfilled fields/images (backend-first) — v2
async function ensureFullCards_v2(rows: WorkingRow[]): Promise<WorkingRow[]> {
  const cache = new Map<string, any>();
  const out = [...rows];
  const tasks = out.map(async (r, i) => {
    if (r.scryfall_full && r.scryfall_full.id) return;
    let full: any | null = null;

    // 1) by scryfall_id via backend
    if (r.scryfall_id) {
      if (cache.has(r.scryfall_id)) full = cache.get(r.scryfall_id);
      else {
        try { full = await scryApi.getCardById(r.scryfall_id); } catch {}
        cache.set(r.scryfall_id, full || null);
      }
    }
    // 2) by oracle_id via backend search
    if (!full && r.oracle_id) {
      try {
        const list = await printsByOracleSafe(r.oracle_id);
        full = sortByRelease(list)[0] ?? null;
      } catch {}
    }
    // 3) by name via backend search (no "!" exact; backend-only)
    if (!full && r.name) {
      try {
        const nm = normExactName(r.name);
        full = await namedSafe(nm);
        if (!full) {
          const arr = await searchPrintsSafe(`name:"${nm}"`);
          full = sortByRelease(arr)[0] ?? null;
        }
        if (!full) {
          const arr2 = await searchPrintsSafe(`"${nm}"`);
          full = sortByRelease(arr2)[0] ?? null;
        }
      } catch {}
    }

    if (!full) return;

    const imgs = pickCardImagesFromFull(full);
    out[i] = {
      ...out[i],
      scryfall_id: out[i].scryfall_id || full.id || null,
      oracle_id: out[i].oracle_id || full.oracle_id || null,
      set: out[i].set || full.set?.toUpperCase?.() || null,
      number: out[i].number || full.collector_number || null,
      layout: out[i].layout ?? full.layout ?? null,
      type_line: out[i].type_line ?? full.type_line ?? null,
      colors: out[i].colors ?? (Array.isArray(full.colors) ? full.colors : null),
      color_identity: out[i].color_identity ?? (Array.isArray(full.color_identity) ? full.color_identity : null),
      mana_value:
        out[i].mana_value ??
        (typeof full.mana_value === "number" ? full.mana_value : (typeof full.cmc === "number" ? full.cmc : null)),
      oracle_text: out[i].oracle_text ?? full.oracle_text ?? null,
      image_small: out[i].image_small ?? imgs.small,
      image_normal: out[i].image_normal ?? imgs.normal,
      image_large: out[i].image_large ?? imgs.large,
      scryfall_full: full,
    };
  });
  await Promise.all(tasks);
  return out;
}

// Use search oracleid: for prints (use the wrapper so it mirrors V2/QSO)
async function backendPrintsByOracle(oracleId: string): Promise<any[]> {
  return await apiPrintsByOracle(oracleId);
}

// Backend by exact name (use namedSafe first; then searchPrintsSafe)
async function backendByExactName(name: string): Promise<any | null> {
  const nm = normExactName(name);
  const want = nameKey(nm);

  // 1) Named lookup (backend-safe)
  const named = await namedSafe(nm);
  if (named?.id) return named;

  // 2) Search prints safely
  let arr: any[] = await searchPrintsSafe(`name:"${nm}"`);
  if (!arr.length) arr = await searchPrintsSafe(`"${nm}"`);
  arr = sortByRelease(arr);

  if (!arr.length) return null;
  return arr.find(c => nameKey(c?.name) === want) || arr[0] || null;
}

// BEGIN component wrapper (make sure the following code is INSIDE the component)
export default function ImportDeckModal({ open, onClose }: Props) {
  // NOTE: your state, refs and handlers (rows, deck, internalOpen, mode, input, loading, dest,
  // excludeBasics, forceSingleCopies, canImport, totalSelected, setRows, setDeck, handleParse,
  // variantOpenIndex, variants, variantLoading, variantContainerRef, activeVariantFetch, etc.)
  // must be declared above this comment, inside this function.

  // State and refs (split out from component body for clarity)
  const [internalOpen, setInternalOpen] = useState(open);
  const [mode, setMode] = useState<"text"|"link">("text");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dest, setDest] = useState<Destination>("collection");
  // Target pickers for binder/deck
  const [binders, setBinders] = useState<{id:string; name:string}[]>([]);
  const [decks, setDecks] = useState<{id:string; name:string}[]>([]);
  const [selectedBinderId, setSelectedBinderId] = useState<string>("");
  const [selectedDeckId, setSelectedDeckId] = useState<string>("");
  const [newBinderName, setNewBinderName] = useState<string>("");
  const [newDeckName, setNewDeckName] = useState<string>("");
  const [excludeBasics, setExcludeBasics] = useState(true);
  const [forceSingleCopies, setForceSingleCopies] = useState(true);
  const [rows, setRows] = useState<WorkingRow[]>([]);
  const [deck, setDeck] = useState<NormalDeck | null>(null);
  const [variantOpenIndex, setVariantOpenIndex] = useState<number | null>(null);
  const [variants, setVariants] = useState<VariantPrint[]>([]);
  const [variantLoading, setVariantLoading] = useState(false);
  const variantContainerRef = useRef<HTMLDivElement | null>(null);
  const activeVariantFetch = useRef<{ index: number; token: number } | null>(null);

  // NEW: keep internalOpen in sync with prop
  useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  // Load existing binders/decks when opening
  useEffect(() => {
    async function load() {
      const u = auth.currentUser;
      if (!u) return;
      try {
        const bs = await getDocs(query(collection(db, "users", u.uid, "binders"), orderBy("createdAt","desc")));
        setBinders(bs.docs.map(d => ({ id: d.id, name: (d.data()?.name || d.data()?.title || "Untitled Binder") as string })));
      } catch {}
      try {
        const ds = await getDocs(query(collection(db, "users", u.uid, "decks"), orderBy("createdAt","desc")));
        setDecks(ds.docs.map(d => ({ id: d.id, name: (d.data()?.name || d.data()?.title || "Untitled Deck") as string })));
      } catch {}
    }
    if (internalOpen) load();
  }, [internalOpen]);

  // NEW: implement resetState used by handleClose
  function resetState() {
    setMode("text");
    setInput("");
    setDeck(null);
    setRows([]);
    setVariantOpenIndex(null);
    setVariants([]);
    setVariantLoading(false);
    activeVariantFetch.current = null;
    setExcludeBasics(true);
    setForceSingleCopies(true);
    setSelectedBinderId("");
    setSelectedDeckId("");
    setNewBinderName("");
    setNewDeckName("");
  }

  // Derived and memoized values
  const totalSelected = useMemo(() => rows.filter(r=>r.include).reduce((s,c)=>s+c.count,0), [rows]);
  const canImport = useMemo(() => rows.filter(r => r.include && r.count > 0).length > 0, [rows]);

  // NEW: validate import target inside the component
  const validTarget = useMemo(() => {
    if (!rows.length) return false;
    if (dest === "binder") return Boolean(selectedBinderId || newBinderName.trim());
    if (dest === "deck") return Boolean(selectedDeckId || newDeckName.trim());
    return true; // collection/wishlist
  }, [rows.length, dest, selectedBinderId, newBinderName, selectedDeckId, newDeckName]);

  // --- Handlers ---
  const handleParse = useCallback(async () => {
    const raw = input.trim();
    if (!raw) return;

    // reset variant panel and previous results
    setVariantOpenIndex(null);
    setVariants([]);
    setVariantLoading(false);

    setLoading(true);
    try {
      // 1) Parse deck from text or URL
      let parsed: NormalDeck | null = null;
      if (mode === "link") {
        parsed = await parseDeckFromUrl(raw);
      } else {
        parsed = await parseDeckFromText(raw);
      }
      if (!parsed) throw new Error("Could not parse input.");
      if (!Array.isArray(parsed.main) || parsed.main.length === 0) {
        throw new Error("No cards found in input.");
      }

      // 2) Enrich with Scryfall so we have ids/images for preview
      let enriched: NormalDeck = parsed;
      try {
        enriched = await enrichWithScryfall(parsed);
      } catch (e) {
        console.warn("[import] enrichWithScryfall failed, continuing with parsed deck only:", e);
      }

      // 3) Build WorkingRow[] for UI
      const newRows: WorkingRow[] = (enriched.main || parsed.main).map((c: NormalCard): WorkingRow => {
        const include = excludeBasics ? !isBasicLand(c.name) : true;
        const count = forceSingleCopies ? 1 : Math.max(1, Number((c as any).count) || 1);
        return {
          ...c,
          include,
          count,
          foil: Boolean((c as any).foil) || false,
          // Default all imports to be available for both sale and trade
          for_trade: true,
          for_sale: true,
          // keep any enrichment if present
          scryfall_full: (c as any).scryfall_full || null,
          image_small: (c as any).image_small || null,
          image_normal: (c as any).image_normal || null,
          image_large: (c as any).image_large || null,
        };
      });

      // 4) Save to state to render grid
      // backfill images/ids from backend so previews render consistently
      const hydrated = await ensureFullCards_v2(newRows);
      setDeck(enriched);
      setRows(hydrated);
    } catch (err: any) {
      console.error("[import] preview failed:", err);
      alert(err?.message || "Failed to parse input.");
      setDeck(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [input, mode, excludeBasics, forceSingleCopies]);

  // Variant helpers
  const openVariantsFor = useCallback(async (index: number) => {
    const row = rows[index];
    if (!row) return;
    const token = Date.now();
    activeVariantFetch.current = { index, token };

    setVariantOpenIndex(index);
    setVariantLoading(true);
    try {
      const prints = await fetchAllPrintVariants(
        row.name,
        row.oracle_id || row.scryfall_full?.oracle_id || null,
        row.scryfall_id || row.scryfall_full?.id || null,
        row.set || row.scryfall_full?.set || null,
        row.number || row.scryfall_full?.collector_number || null
      );

      // ensure still current before applying
      if (!activeVariantFetch.current || activeVariantFetch.current.token !== token || activeVariantFetch.current.index !== index) return;

      // Put current at first if present
      if (row.scryfall_id) {
        const idx = prints.findIndex(p => p.id === row.scryfall_id);
        if (idx > 0) {
          const current = prints.splice(idx,1)[0];
          prints.unshift(current);
        }
      } else {
        // If no current id, synthesize current if not found
        const exists = prints.some(p => p.set === row.set && p.number === row.number);
        const currentId = row.scryfall_id || `current-${row.name}-${row.set || "NOSET"}-${row.number || "NOCN"}`;
        if (!exists && !prints.some(p => p.id === currentId)) {
          prints.unshift({
            id: currentId,
             oracle_id: row.oracle_id || null,
             name: row.name,
             set: row.set || null,
             number: row.number || null,
             image_small: row.image_small || null,
             image_normal: row.image_normal || null,
             image_large: row.image_large || null,
             layout: row.layout || null,
             type_line: row.type_line || null,
             colors: row.colors || null,
             color_identity: row.color_identity || null,
             mana_value: row.mana_value || null,
             oracle_text: row.oracle_text || null,
           });
         }
      }
      setVariants(prints);
    } finally {
      // only clear loading if still the active panel
      if (activeVariantFetch.current?.index === index) setVariantLoading(false);
    }
  }, [rows]);

  const closeVariants = () => {
    setVariantOpenIndex(null);
    setVariants([]);
    setVariantLoading(false);
  };

  const applyVariant = (variant: VariantPrint) => {
    if (variantOpenIndex == null) return;
    setRows(r => r.map((row, i) => {
      if (i !== variantOpenIndex) return row;
      return {
        ...row,
        scryfall_id: variant.id,
        oracle_id: variant.oracle_id || row.oracle_id,
        set: variant.set,
        number: variant.number,
        image_small: variant.image_small,
        image_normal: variant.image_normal,
        image_large: variant.image_large,
        layout: variant.layout ?? row.layout,
        type_line: variant.type_line ?? row.type_line,
        colors: variant.colors ?? row.colors,
        color_identity: variant.color_identity ?? row.color_identity,
        mana_value: variant.mana_value ?? row.mana_value,
        oracle_text: variant.oracle_text ?? row.oracle_text,
      };
    }));
    closeVariants(); // NEW: auto-close after selection
  };

  // Re-apply basic land exclusion when toggled
  useEffect(() => {
    setRows(rs => rs.map(r => {
      if (excludeBasics && isBasicLand(r.name)) return { ...r, include: false };
      if (!excludeBasics && isBasicLand(r.name) && !r.include) return { ...r, include: true };
      return r;
    }));
  }, [excludeBasics]);

  function bulkSelect(v: boolean) {
    setRows(r => r.map(x => {
      if (excludeBasics && isBasicLand(x.name)) return { ...x, include: false };
      return { ...x, include: v };
    }));
  }
  function bulkToggleFoils() {
    setRows(r => r.map(x => ({ ...x, foil: !x.foil })));
  }

  function updateRow(i: number, patch: Partial<WorkingRow>) {
    setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  }

  function handleClose() {
    setInternalOpen(false);
    resetState();
    onClose?.();
  }

  async function handleImport() {
    const u = auth.currentUser;
    if (!u || !deck) return alert("Sign in first");
    let final = rows.filter(r => r.include && r.count > 0);
    if (!final.length) { alert("Nothing selected"); return; }
    setLoading(true);
    try {
      // Ensure every row has full backend card data before writing
      final = await ensureFullCards_v2(final);

      if (dest === "wishlist") {
        await importWishlist(u.uid, final);
      } else if (dest === "collection") {
        await importCollection(u.uid, final);
      } else if (dest === "binder") {
        // 1) Write cards into collection
        const createdIds = await importCollectionAndReturnIds(u.uid, final);
        // 2) Create or use target binder
        const binderId = selectedBinderId || await createBinder(u.uid, newBinderName.trim() || deck.title || "New Binder");
        // 3) Add slots pointing at the newly created card docs
        await addSlotsToBinder(u.uid, binderId, createdIds);
      } else if (dest === "deck") {
        // 1) Write cards into collection
        const createdIds = await importCollectionAndReturnIds(u.uid, final);

        // 1a) Build a quick index of createdIds per row (in order)
        let cursor = 0;
        const idsByRow = new Map<number, string[]>();
        for (let i = 0; i < final.length; i++) {
          const cnt = Math.max(1, Number(final[i].count) || 1);
          idsByRow.set(i, createdIds.slice(cursor, cursor + cnt));
          cursor += cnt;
        }

        // 1b) Derive color identity union from rows
        const ciSet = new Set<string>();
        for (const r of final) {
          if (Array.isArray(r.color_identity)) r.color_identity.forEach(x => x && ciSet.add(String(x)));
          else if (Array.isArray(r.colors)) r.colors.forEach(x => x && ciSet.add(String(x)));
        }
        const color_identity = Array.from(ciSet);

        // 1c) Try to find commander card id and cover image
        const commanderName = Array.isArray(deck.commander) && deck.commander.length ? deck.commander[0] : "";
        let commander_card_id: string | null = null;
        let cover_image: string | null = null;

        const nameMatchKey = commanderName ? nameKey(commanderName) : "";
        const commanderRowIdx = nameMatchKey
          ? final.findIndex(r => nameKey(r.name) === nameMatchKey)
          : -1;

        if (commanderRowIdx >= 0) {
          const ids = idsByRow.get(commanderRowIdx) || [];
          commander_card_id = ids[0] || null;
          cover_image = previewUrl(final[commanderRowIdx]) || null;
        }
        if (!cover_image && final.length) {
          cover_image = previewUrl(final[0]) || null;
        }

        // 2) Create or use target deck with full metadata
        const deckName = newDeckName.trim() || deck.title || "New Deck";
        const deckId = selectedDeckId || await createDeck(u.uid, deckName, {
          format: deck.format ?? null,
          color_identity,
          commander_card_id: commander_card_id ?? null,
          coverCardId: null,
          cover_image: cover_image ?? null,
          public: true,
          slug: slugify(deckName),
        });

        // 3) Add slots pointing at the newly created card docs
        await addSlotsToDeck(u.uid, deckId, createdIds);
      }

      try {
        await addDoc(collection(db,"users",u.uid,"imports"), {
          source: mode,
          dest,
          targetName: dest==="binder" ? (newBinderName || binders.find(b=>b.id===selectedBinderId)?.name || null)
                  : dest==="deck" ? (newDeckName || decks.find(d=>d.id===selectedDeckId)?.name || null)
                  : null,
          stats: { main: final.length, totalMain: final.reduce((s,c)=>s+c.count,0) },
          createdAt: serverTimestamp(),
        });
      } catch {}
      setDeck(null);
      setRows([]);
      setInput("");
      handleClose();
    } catch (e: any) {
      alert(e?.message || "Import failed");
    } finally { setLoading(false); }
  }

  // === RENDER (unchanged header/footer, insert variant strip) ===
  return (
    <Dialog
      open={internalOpen}
      onOpenChange={(v)=>{
        if (!v) handleClose();
        else setInternalOpen(true);
      }}
    >
      <DialogContent
        className="max-w-5xl w-[92vw] p-0 overflow-hidden"
        onEscapeKeyDown={handleClose}
        onPointerDownOutside={(e) => {
          const t = (e as any).target as Node | null;
          if (t && variantContainerRef.current?.contains(t)) {
            e.preventDefault();
            return;
          }
          handleClose();
        }}
        onInteractOutside={(e) => {
          const t = (e as any).target as Node | null;
          if (t && variantContainerRef.current?.contains(t)) {
            e.preventDefault();
            return;
          }
          handleClose();
        }}
      >
        <div className="flex flex-col h-[85vh]">
          {/* Header (unchanged) */}
          <div className="px-5 pt-5 pb-3 border-b border-white/15">
            <DialogHeader>
              <DialogTitle className="text-lg">Import Cards</DialogTitle>
            </DialogHeader>
            {/* Input area (modified to add left image column) */}
            <div className="mt-3 flex gap-4">
              <div className="flex-shrink-0 flex items-start">
                <img
                  src="/images/happy.png"
                  alt="Happy"
                  className="w-20 h-20 object-contain select-none pointer-events-none"
                  draggable={false}
                />
              </div>
              <div className="flex flex-col gap-3 flex-1">
                <div className="flex flex-wrap gap-2 items-center">
                  <Button size="sm" variant={mode==="text"?"default":"outline"} disabled={loading} onClick={()=>setMode("text")}>Paste Text</Button>
                  <Button size="sm" variant={mode==="link"?"default":"outline"} disabled={loading} onClick={()=>setMode("link")}>Import from Link</Button>
                  <span className="text-[11px] opacity-70">
                    Archidekt, Moxfield, TappedOut, ManaBox, AetherHub & EDHRec. TCGPlayer coming soon.
                  </span>
                  <label className="ml-auto inline-flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      className="accent-emerald-500"
                      checked={excludeBasics}
                      onChange={e=>setExcludeBasics(e.target.checked)}
                      disabled={loading || !rows.length}
                    />
                    Exclude Basic Lands
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      className="accent-emerald-500"
                      checked={forceSingleCopies}
                      onChange={e=>setForceSingleCopies(e.target.checked)}
                      disabled={loading}
                    />
                    Set counts to 1
                  </label>
                </div>
                {mode === "text" ? (
                  <textarea
                    className="w-full h-28 border rounded p-2 text-xs resize-none bg-white text-black placeholder:text-gray-500"
                    placeholder="4 Lightning Bolt
1 Sol Ring
..."
                    value={input}
                    disabled={loading}
                    onChange={e=>setInput(e.target.value)}
                  />
                ) : (
                  <input
                    className="w-full border rounded p-2 text-sm bg-white text-black placeholder:text-gray-500"
                    placeholder="Deck URL (Archidekt / Moxfield / etc)"
                    value={input}
                    disabled={loading}
                    onChange={e=>setInput(e.target.value)}
                  />
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <Button size="sm" onClick={handleParse} disabled={loading || !input.trim()}>
                    {loading ? "Importing preview..." : "Preview"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={()=>{ setDeck(null); setRows([]); setInput(""); }} disabled={loading}>Clear</Button>
                  <div className="ml-auto flex flex-col gap-2 text-xs min-w-[280px]">
                    <div className="flex items-center gap-2">
                      <span>Destination:</span>
                      <select
                        className="border rounded px-2 py-1 bg-white text-black"
                        value={dest}
                        disabled={loading}
                        onChange={e=>setDest(e.target.value as Destination)}
                      >
                        <option value="collection">Collection</option>
                        <option value="wishlist">Wishlist</option>
                        <option value="binder">Binder</option>
                        <option value="deck">Deck</option>
                      </select>
                    </div>
                    {(dest === "binder") && (
                      <div className="flex items-center gap-2">
                        <span>Binder:</span>
                        <select
                          className="border rounded px-2 py-1 bg-white text-black flex-1"
                          value={selectedBinderId || (newBinderName ? "__new__" : "")}
                          onChange={(e)=>{
                            const v = e.target.value;
                            if (v === "__new__") { setSelectedBinderId(""); }
                            else { setSelectedBinderId(v); setNewBinderName(""); }
                          }}
                          disabled={loading}
                        >
                          <option value="">Select existing…</option>
                          {binders.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          <option value="__new__">+ Add New Binder…</option>
                        </select>
                        {(selectedBinderId === "" ) && (
                          <input
                            className="border rounded px-2 py-1 bg-white text-black flex-1"
                            placeholder="New binder name"
                            value={newBinderName}
                            onChange={(e)=>setNewBinderName(e.target.value)}
                            disabled={loading}
                          />
                        )}
                      </div>
                    )}
                    {(dest === "deck") && (
                      <div className="flex items-center gap-2">
                        <span>Deck:</span>
                        <select
                          className="border rounded px-2 py-1 bg-white text-black flex-1"
                          value={selectedDeckId || (newDeckName ? "__new__" : "")}
                          onChange={(e)=>{
                            const v = e.target.value;
                            if (v === "__new__") { setSelectedDeckId(""); }
                            else { setSelectedDeckId(v); setNewDeckName(""); }
                          }}
                          disabled={loading}
                        >
                          <option value="">Select existing…</option>
                          {decks.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          <option value="__new__">+ Add New Deck…</option>
                        </select>
                        {(selectedDeckId === "" ) && (
                          <input
                            className="border rounded px-2 py-1 bg-white text-black flex-1"
                            placeholder="New deck name"
                            value={newDeckName}
                            onChange={(e)=>setNewDeckName(e.target.value)}
                            disabled={loading}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-4 pb-4">
            {deck && (
              <>
                <DeckInfoBar
                  deck={deck}
                  rows={rows}
                  bulkSelect={bulkSelect}
                  bulkToggleFoils={bulkToggleFoils}
                />
                <CardGrid
                  rows={rows}
                  updateRow={updateRow}
                  onOpenVariants={openVariantsFor}
                  variantOpenIndex={variantOpenIndex}
                  variants={variants}
                  variantLoading={variantLoading}
                  onCloseVariants={closeVariants}
                  onChooseVariant={applyVariant}
                  variantContainerRef={variantContainerRef}   // NEW
                />
              </>
            )}
            {!deck && (
              <div className="text-sm opacity-70 pt-6">
                Paste text or enter a deck URL, then click Preview.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-white/15 px-5 py-3 flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
            </DialogClose>
            <Button onClick={handleImport} disabled={!canImport || loading || !validTarget}>
              {loading ? "Importing..." : `Import ${totalSelected} to ${labelForDest(dest)}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} // END component wrapper

// NEW info / control bar
function DeckInfoBar({ deck, rows, bulkSelect, bulkToggleFoils }:{
  deck: NormalDeck;
  rows: WorkingRow[];
  bulkSelect: (v:boolean)=>void;
  bulkToggleFoils: ()=>void;
}) {
  const totalSelected = rows.filter(r=>r.include).reduce((s,c)=>s+c.count,0);
  const allSelected = rows.length>0 && rows.every(r=>r.include || (isBasicLand(r.name)));
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
      <div className="font-semibold text-sm">
        {deck.title || "Untitled"} {deck.format && <span className="opacity-70">• {deck.format}</span>}
      </div>
      {deck.commander?.length ? <span className="opacity-70">Commander: {deck.commander.join(", ")}</span> : null}
      {deck.companion ? <span className="opacity-70">Companion: {deck.companion}</span> : null}
      <span className="ml-auto opacity-70">{totalSelected} selected</span>
      <Button size="xs" variant="outline" onClick={()=>bulkSelect(true)} disabled={allSelected}>Select All</Button>
      <Button size="xs" variant="outline" onClick={()=>bulkSelect(false)}>Deselect All</Button>
      <Button size="xs" variant="outline" onClick={bulkToggleFoils}>Toggle Foils</Button>
    </div>
  );
}

// Modified CardGrid to support variant opening
function CardGrid({
  rows,
  updateRow,
  onOpenVariants,
  variantOpenIndex,
  variants,
  variantLoading,
  onCloseVariants,
  onChooseVariant,
  variantContainerRef
}: {
  rows: WorkingRow[];
  updateRow: (i:number,p:Partial<WorkingRow>)=>void;
  onOpenVariants: (i:number)=>void;
  variantOpenIndex: number | null;
  variants: VariantPrint[];
  variantLoading: boolean;
  onCloseVariants: () => void;
  onChooseVariant: (v: VariantPrint) => void;
  variantContainerRef: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 items-start">
      {rows.map((r,i)=>(
        <React.Fragment key={i}>
          <div
            className={`relative group rounded-md overflow-hidden ring-1 transition
            ${r.include ? "ring-emerald-400" : "ring-white/15 opacity-60 hover:opacity-100"}`}
          >
            <div
              className="aspect-[63/88] w-full bg-neutral-900 flex items-center justify-center overflow-hidden cursor-pointer"
              title="Click to toggle include"
              onClick={()=>updateRow(i,{ include: !r.include })}
            >
              {(() => {
                const img = previewUrl(r);
                return img ? (
                  <img
                    src={img}
                    alt={r.name}
                    className="w-full h-full object-contain pointer-events-none select-none"
                    loading="lazy"
                  />
                ) : (
                  <span className="px-2 text-center text-[11px]">{r.name}</span>
                );
              })()}
            </div>

            <div className="absolute top-1 left-1 flex gap-1">
              {r.for_sale && <span className="bg-amber-500/90 text-black px-1 rounded text-[10px]">Sale</span>}
              {r.for_trade && <span className="bg-blue-500/90 text-black px-1 rounded text-[10px]">Trade</span>}
            </div>
            <div className="absolute top-1 right-1">
              <span className={`inline-flex w-5 h-5 items-center justify-center rounded-full text-[10px] font-bold
                ${r.include ? "bg-emerald-500 text-black" : "bg-white/25 text-white"}`}>
                {r.include ? "✓" : "✕"}
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 bg-black/75 backdrop-blur-sm p-1 flex flex-col gap-1">
              <button
                data-variant-trigger
                className="w-full text-[10px] px-2 py-1 rounded bg-fuchsia-600/80 hover:bg-fuchsia-500 text-black font-semibold transition"
                onClick={(e)=>{ e.stopPropagation(); onOpenVariants(i); }}
                aria-label="Select version"
              >
                {variantOpenIndex === i ? "Change Version" : "Select Version"}
              </button>
              <div className="flex items-center justify-between gap-1">
                <span className="truncate text-[11px] font-medium" title={r.name}>{r.name}</span>
                <div className="flex gap-1">
                  <button
                    className={`px-1 rounded text-[10px] ${r.foil ? "bg-fuchsia-500 text-black" : "bg-white/15"}`}
                    onClick={()=>updateRow(i,{ foil: !r.foil })}
                    title="Toggle foil"
                    aria-label="Toggle foil"
                  >Foil</button>
                  <button
                    className="px-1 rounded text-[10px] bg-white/15 hover:bg-white/25"
                    onClick={()=>updateRow(i,{ include: !r.include })}
                    title="Toggle include"
                    aria-label="Toggle include"
                  >{r.include ? "On" : "Off"}</button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    className="w-5 h-5 rounded bg-white/15 hover:bg-white/25"
                    onClick={()=>updateRow(i,{ count: Math.max(1, r.count - 1) })}
                    title="Decrease"
                    aria-label="Decrease count"
                  >-</button>
                  <input
                    type="number"
                    min={1}
                    value={r.count}
                    className="w-10 bg-transparent border border-white/25 rounded px-1 text-center text-[11px]"
                    onChange={e=>updateRow(i,{ count: Math.max(1, Number(e.target.value)||1) })}
                    aria-label="Card count"
                  />
                  <button
                    className="w-5 h-5 rounded bg-white/15 hover:bg-white/25"
                    onClick={()=>updateRow(i,{ count: r.count + 1 })}
                    title="Increase"
                    aria-label="Increase count"
                  >+</button>
                </div>
                <div className="flex gap-1">
                  <button
                    className={`px-1 rounded text-[10px] ${r.for_sale ? "bg-amber-500 text-black" : "bg-white/15"}`}
                    onClick={()=>updateRow(i,{ for_sale: !r.for_sale })}
                    title="Toggle sale"
                    aria-label="Toggle sale"
                  >S</button>
                  <button
                    className={`px-1 rounded text-[10px] ${r.for_trade ? "bg-blue-500 text-black" : "bg-white/15"}`}
                    onClick={()=>updateRow(i,{ for_trade: !r.for_trade })}
                    title="Toggle trade"
                    aria-label="Toggle trade"
                  >T</button>
                </div>
              </div>
            </div>

            {variantOpenIndex === i && (
              <div className="absolute inset-0 pointer-events-none ring-2 ring-fuchsia-500/70 rounded-md" />
            )}
          </div>

          {variantOpenIndex === i && (
            <div
              ref={el => { if (variantOpenIndex === i) variantContainerRef.current = el; }}
              className="col-span-full -mt-2 mb-2 p-3 rounded-md border border-white/15 bg-neutral-900/80"
            >
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-sm font-semibold">
                  Versions for: <span className="opacity-80">{r.name}</span>
                </h4>
                <span className="text-[11px] opacity-60">
                  {variantLoading ? "Loading..." : `${variants.length} print${variants.length!==1?"s":""}`}
                </span>
                <button
                  onClick={onCloseVariants}
                  className="ml-auto text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >Close</button>
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <div className="flex gap-3">
                  {variantLoading && <div className="text-[11px] opacity-70 py-4">Fetching printings...</div>}
                  {!variantLoading && variants.map(v=>{
                    const active = v.id === r.scryfall_id;
                    const img = previewUrl(v);
                    return (
                      <button
                        key={`${v.id}-${v.set || ""}-${v.number || ""}`}
                        className={`flex flex-col items-center gap-1 min-w-[110px] p-2 rounded border text-[10px] ${
                          active ? "border-fuchsia-500 bg-fuchsia-500/15"
                                 : "border-white/15 hover:border-white/40"
                        }`}
                        onClick={()=>onChooseVariant(v)}
                        title={`${v.set || "SET"} ${v.number || ""}`}
                      >
                        <div className="w-[96px] h-[135px] bg-neutral-800 rounded overflow-hidden flex items-center justify-center">
                          {img
                            ? <img src={img} alt={r.name} className="w-full h-full object-contain" loading="lazy" />
                            : <span className="px-1">{r.name}</span>}
                        </div>
                        <div className="w-full text-center leading-tight">
                          <div className="truncate">{(v.set || "").toUpperCase()} {v.number || ""}</div>
                          {active && <div className="text-fuchsia-400 font-semibold">Selected</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Utility to gather all print variants from Scryfall (cached)
type VariantPrint = {
  id: string;
  oracle_id?: string | null;
  name: string;
  set: string | null;
  number: string | null;
  image_small: string | null;
  image_normal: string | null;
  image_large: string | null;
  layout?: string | null;
  type_line?: string | null;
  colors?: string[] | null;
  color_identity?: string[] | null;
  mana_value?: number | null;
  oracle_text?: string | null;
};

const variantCache = new Map<string, VariantPrint[]>();

// Variants: backend only (oracle → prints; else id → prints; else set+cn; else name → base → prints)
async function fetchAllPrintVariants(
  cardName: string,
  oracleId?: string | null,
  scryfallId?: string | null,
  set?: string | null,
  cn?: string | null
): Promise<VariantPrint[]> {
  const key = [
    cardName.toLowerCase(),
    oracleId || "no-oracle",
    scryfallId || "no-id",
    (set || "no-set").toLowerCase(),
    cn || "no-cn",
  ].join("::");

  if (variantCache.has(key)) return variantCache.get(key)!;

  // Local helpers to avoid scope issues with HMR
  const norm = (s: string): string => {
    const n = String(s || "")
      .replace(/&(?:#x27|#39|#x2019|#8217);/gi, "'")
      .replace(/[’‘`´]/g, "'")
      .replace(/''+/g, "'")
      .replace(/"{2,}/g, '"')
      .trim();
    return n;
  };
  const sortRel = <T extends { released_at?: string; set?: string; collector_number?: string }>(list: T[]) => {
    return [...(list || [])].sort((a, b) => {
      const da = a.released_at ? Date.parse(a.released_at) : 0;
      const db = b.released_at ? Date.parse(b.released_at) : 0;
      if (db !== da) return db - da;
      const sa = a.set ?? "";
      const sb = b.set ?? "";
      if (sb !== sa) return sb.localeCompare(sa);
      const ca = parseInt((a as any).collector_number || "0", 10) || 0;
      const cb = parseInt((b as any).collector_number || "0", 10) || 0;
      return cb - ca;
    });
  };

  let printsData: any[] = [];

  // 1) oracle → prints
  if (!printsData.length && oracleId) {
    printsData = await apiPrintsByOracle(oracleId);
  }

  // 2) printing id → prints
  if (!printsData.length && scryfallId) {
    printsData = await apiPrintsById(scryfallId);
  }

  // 3) set + collector number → base → prints
  if (!printsData.length && set && cn) {
    const qcn = quoteCollectorNumber(cn);
    const q = qcn ? `set:${String(set).toLowerCase()} cn:${qcn}` : `set:${String(set).toLowerCase()}`;
    const arr = sortRel(await searchPrintsSafe(q));
    const base = arr[0] || null;
    if (base?.oracle_id) printsData = await apiPrintsByOracle(base.oracle_id);
    else if (base?.id) printsData = await scryApi.getCardById(base.id);
  }

  // 4) name → base → prints
  if (!printsData.length && cardName) {
    const nm = norm(cardName);
    let base: any | null = await namedSafe(nm);
    if (!base) {
      const arr = sortRel(await searchPrintsSafe(`name:"${nm}"`));
      base = arr[0] || null;
    }
    if (!base) {
      const arr2 = sortRel(await searchPrintsSafe(`"${nm}"`));
      base = arr2[0] || null;
    }
    if (base?.oracle_id) printsData = await apiPrintsByOracle(base.oracle_id);
    else if (base?.id) printsData = await apiPrintsById(base.id);
  }

  const all: VariantPrint[] = (printsData || []).map((c: any) => {
    const imgs = pickCardImagesFromFull(c);
    return {
      id: c?.id,
      oracle_id: c?.oracle_id ?? null,
      name: c?.name || cardName,
      set: c?.set ? String(c.set).toUpperCase() : (c?.set_code ? String(c.set_code).toUpperCase() : null),
      number: c?.collector_number ?? c?.number ?? null,
      image_small: imgs.small || c?.image_uris?.small || c?.card_faces?.[0]?.image_uris?.small || null,
      image_normal: imgs.normal || c?.image_uris?.normal || c?.card_faces?.[0]?.image_uris?.normal || null,
      image_large: imgs.large || c?.image_uris?.large || c?.card_faces?.[0]?.image_uris?.large || null,
      layout: c?.layout ?? null,
      type_line: c?.type_line ?? null,
      colors: Array.isArray(c?.colors) ? c.colors : null,
      color_identity: Array.isArray(c?.color_identity) ? c.color_identity : null,
      mana_value: typeof c?.mana_value === "number" ? c.mana_value : (typeof c?.cmc === "number" ? c.cmc : null),
      oracle_text: c?.oracle_text ?? null,
    };
  });

  if (all.length) variantCache.set(key, all);
  return all;
}

// Firestore batch chunking to avoid 500 write limit
async function commitBatches(ops: ((b: ReturnType<typeof writeBatch>) => void)[]) {
  const CHUNK = 450; // headroom
  for (let i = 0; i < ops.length; i += CHUNK) {
    const batch = writeBatch(db);
    ops.slice(i, i + CHUNK).forEach(fn => fn(batch));
    await batch.commit();
  }
}

// Create cards in the user's collection and return their IDs (1 doc per copy)
async function importCollectionAndReturnIds(uid: string, cards: WorkingRow[]): Promise<string[]> {
  const ids: string[] = [];
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];
  const now = serverTimestamp();

  for (const c of cards) {
    for (let i = 0; i < c.count; i++) {
      ops.push((batch) => {
        const ref = doc(collection(db, "users", uid, "cards"));
        ids.push(ref.id);
        // Ensure defaults even if upstream rows were missing flags
        const sale = (c.for_sale ?? true) === true;
        const trade = (c.for_trade ?? true) === true;
        batch.set(ref, {
          scryfall_id: c.scryfall_id || null,
          oracle_id: c.oracle_id || null,
          name: c.name,
          name_lower: c.name.toLowerCase(),
          set: c.set || null,
          number: c.number || null,
          image: c.image_normal || c.image_large || c.image_small || null,
          image_small: c.image_small || null,
          image_normal: c.image_normal || null,
          image_large: c.image_large || null,
          type_line: c.type_line || null,
          colors: c.colors || null,
          color_identity: c.color_identity || null,
          mana_value: c.mana_value ?? null,
          oracle_text: c.oracle_text || null,
          layout: c.layout || null,
          scryfall_full: c.scryfall_full || null,
          foil: c.foil || false,
          for_trade: trade,
          for_sale: sale,
          // If both are true, prefer "sale" as the primary status
          status: (sale || trade) ? (sale ? "sale" : "trade") : "keep",
          createdAt: now,
        });
      });
    }
  }

  await commitBatches(ops);
  return ids;
}

// Keep existing importCollection for direct "Collection" destination
async function importCollection(uid: string, cards: WorkingRow[]) {
  await importCollectionAndReturnIds(uid, cards);
}

// Link existing collection cards into a Deck as slots
async function addSlotsToDeck(uid: string, deckId: string, cardIds: string[]) {
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];
  const now = serverTimestamp();
  for (const cardId of cardIds) {
    ops.push((batch) => {
      const ref = doc(collection(db, "users", uid, "decks", deckId, "slots"));
      batch.set(ref, {
        cardId,
        createdAt: now,
      });
    });
  }
  await commitBatches(ops);
}

// Link existing collection cards into a Binder as slots
async function addSlotsToBinder(uid: string, binderId: string, cardIds: string[]) {
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [];
  const now = serverTimestamp();
  for (const cardId of cardIds) {
    ops.push((batch) => {
      const ref = doc(collection(db, "users", uid, "binders", binderId, "slots"));
      batch.set(ref, {
        cardId,
        createdAt: now,
      });
    });
  }
  await commitBatches(ops);
}

// Create a new Binder doc and return its id
async function createBinder(uid: string, name: string): Promise<string> {
  const ref = await addDoc(collection(db, "users", uid, "binders"), {
    name: name || "New Binder",
    title: name || "New Binder",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// Create a new Deck doc and return its id
async function createDeck(uid: string, name: string, meta?: any): Promise<string> {
  const now = serverTimestamp();
  const data = {
    name: name || "New Deck",
    title: name || "New Deck",
    position: 0,
    public: true,
    slug: slugify(name || "new-deck"),
    createdAt: now,
    updatedAt: now,
    ...(meta || {}),
  };
  const ref = await addDoc(collection(db, "users", uid, "decks"), data);
  return ref.id;
}

// ---------- Safe wrappers (backend-first, optional public fallback) ----------
const USE_PUBLIC_FALLBACK =
  String((import.meta as any)?.env?.VITE_SCRY_PUBLIC_FALLBACK || "") === "1" ||
  Boolean((import.meta as any)?.env?.DEV);

// NEW: public fallback for search (no exact name support)
async function publicScrySearch(params: Record<string, string>) {
  if (!USE_PUBLIC_FALLBACK) return null;
  const u = new URL("https://api.scryfall.com/cards/search");
  for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
  const r = await fetch(u.toString(), { headers: { Accept: "application/json" } });
  if (!r.ok) return null;
  try { return await r.json(); } catch { return null; }
}

async function searchPrintsSafe(q: string): Promise<any[]> {
  // 1) backend client
  try {
    if (typeof (scryApi as any).searchPrints === "function") {
      const res = await (scryApi as any).searchPrints(q);
      const arr = toCardArray(res);
      if (arr.length) return arr;
    }
  } catch {}
  // 2) backend proxy route
  try {
    const res2 = await scryCardsGet<any>("search", {
      q, unique: "prints", order: "released", dir: "desc",
    });
    const arr2 = toCardArray(res2);
    if (arr2.length) return arr2;
  } catch {}
  // 3) optional public fallback
  const res3 = await publicScrySearch({ q, unique: "prints", order: "released", dir: "desc" });
  return toCardArray(res3);
}

async function namedSafe(exact: string): Promise<any | null> {
  // 1) backend client
  try {
    if (typeof (scryApi as any).named === "function") {
      const r = await (scryApi as any).named(exact);
      if (r?.id && r?.name) return r;
    }
  } catch {}
  // 2) backend proxy (if present)
  try {
    const r2 = await scryCardsGet<any>("named", { exact });
    if (r2?.id && r2?.name) return r2;
  } catch {}
  // 3) optional public fallback
  if (USE_PUBLIC_FALLBACK) {
    try {
      const u = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(exact)}`;
      const r = await fetch(u, { headers: { Accept: "application/json" } });
      if (r.ok) {
        const js = await r.json();
        if (js?.id && js?.name) return js;
      }
    } catch {}
  }
  return null;
}

async function printsByIdSafe(id: string): Promise<any[]> {
  // 1) backend client
  try {
    if (typeof (scryApi as any).printsById === "function") {
      const r = await (scryApi as any).printsById(id);
      const a = toCardArray(r);
      if (a.length) return a;
    }
  } catch {}
  // 2) backend proxy
  try {
    const r2 = await scryCardsGet<any>(`${encodeURIComponent(id)}/prints`);
    const a2 = toCardArray(r2);
    if (a2.length) return a2;
  } catch {}
  // 3) optional public fallback (disabled by default)
  if (USE_PUBLIC_FALLBACK) {
    try {
      const r3 = await fetch(`https://api.scryfall.com/cards/${encodeURIComponent(id)}/prints`, { headers: { Accept: "application/json" } });
      if (r3.ok) return toCardArray(await r3.json());
    } catch {}
  }
  return [];
}

async function printsByOracleSafe(oracleId: string): Promise<any[]> {
  // 1) backend client
  try {
    if (typeof (scryApi as any).printsByOracle === "function") {
      const r = await (scryApi as any).printsByOracle(oracleId);
      const a = toCardArray(r);
      if (a.length) return a;
    }
  } catch {}
  // 2) backend search
  const fromSearch = await searchPrintsSafe(`oracleid:${oracleId}`);
  if (fromSearch.length) return fromSearch;
  return [];
}

// Robust preview URL resolver for any print/variant or deck row object
function previewUrl(src: any): string {
  // Support rows and variants; look into nested scryfall_full too
  const cand = [
    src?.variant || src,
    src?.scryfall_full,
    src?.variant?.scryfall_full,
  ].find(Boolean) || src;

  const u =
    cand?.image_normal ||
    cand?.image_large ||
    cand?.image_small ||
    cand?.image ||
    cand?.image_uri ||
    cand?.imageUrl ||
    cand?.image_url ||
    cand?.image_uris?.normal ||
    cand?.image_uris?.large ||
    cand?.image_uris?.small ||
    cand?.card_faces?.[0]?.image_uris?.normal ||
    cand?.card_faces?.[0]?.image_uris?.large ||
    cand?.card_faces?.[0]?.image_uris?.small ||
    "";
  return typeof u === "string" ? u : "";
}

// Helper: quote collector numbers that contain letters or slashes
function quoteCollectorNumber(cn?: string | null): string | null {
  if (!cn) return null;
  const s = String(cn).trim();
  if (!s) return null;
  // quote if not a pure integer
  return /^\d+$/.test(s) ? s : `"${s}"`;
}

// NEW: slugify for deck slugs
function slugify(s: string): string {
  return String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

// Variants: backend only (oracle → prints; else id → prints; else set+cn; else name → base → prints)
// Note: unchanged signature; caller already passes all identifiers.
async function fetchAllPrintVariants_legacy(
  cardName: string,
  oracleId?: string | null,
  scryfallId?: string | null,
  set?: string | null,
  cn?: string | null
): Promise<VariantPrint[]> {
  return fetchAllPrintVariants(cardName, oracleId ?? null, scryfallId ?? null, set ?? null, cn ?? null);
}

// Helpers to validate selected destination and label
function labelForDest(d: Destination): string {
  switch (d) {
    case "collection": return "Collection";
    case "wishlist": return "Wishlist";
    case "binder": return "Binder";
    case "deck": return "Deck";
  }
}
